<template>
  <div>
    <CCard>
      <CCardHeader>
        <h5 class="mb-0">Tickets</h5>
        <CButton color="primary" size="sm" class="float-right" @click="openAddTicketModal">Add Ticket</CButton>
      </CCardHeader>
      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show>{{ errorMessage }}</CAlert>
        <div v-if="isLoading" class="text-center py-4">
          <CSpinner color="primary" />
          <div class="small text-muted mt-2">Loading...</div>
        </div>
        <div v-else>
          <CDataTable
            striped
            :items="tickets"
            :fields="ticketFields"
            :no-items-view="{ noItems: 'There are no records to display' }"
          />
          <CRow v-if="totalPages > 1" class="d-flex justify-content-between mt-3">
            <CCol sm="12" md="10">
              <CPagination :active-page.sync="currentPage" :pages="totalPages" @update:activePage="onPageChange" />
            </CCol>
            <CCol sm="12" md="2">
              <form @submit.prevent="goToPage">
                <CFormGroup class="mb-0">
                  <CInputGroup>
                    <CInput v-model="pageInput" />
                    <CInputGroupAppend>
                      <CButton color="primary" @click="goToPage">&raquo;</CButton>
                    </CInputGroupAppend>
                  </CInputGroup>
                </CFormGroup>
              </form>
            </CCol>
          </CRow>
        </div>
      </CCardBody>
    </CCard>

    <!-- Add Ticket Modal -->
    <CModal :show.sync="addTicketModal" color="primary" title="Add New Ticket">
      <CCard class="mb-0">
        <CCardBody>
          <CFormGroup>
            <CLabel>Product Used</CLabel>
            <CSelect v-model="newTicket.product_used" :options="productOptions" />
          </CFormGroup>
          <CFormGroup>
            <CLabel>Crop</CLabel>
            <CInput v-model="newTicket.crop" placeholder="Crop" />
          </CFormGroup>
          <CFormGroup>
            <CLabel>Ticket Type</CLabel>
            <CSelect v-model="newTicket.ticket_type" :options="ticketTypeOptions" />
          </CFormGroup>
          <CFormGroup v-if="newTicket.ticket_type === 'Result'">
            <CLabel>Reason</CLabel>
            <CSelect v-model="newTicket.reason" :options="reasonOptions" />
          </CFormGroup>
          <CFormGroup>
            <CLabel>Customer Mood</CLabel>
            <CSelect v-model="newTicket.customer_mood" :options="moodOptions" />
          </CFormGroup>
          <CFormGroup>
            <CInputCheckbox v-model="newTicket.is_fertilizer_user" />
            <CLabel class="ml-2">Other Fertilizer Used</CLabel>
          </CFormGroup>
          <CFormGroup v-if="newTicket.is_fertilizer_user">
            <CLabel>Other Fertilizer Description</CLabel>
            <CInput v-model="newTicket.description" placeholder="Description" />
          </CFormGroup>
        </CCardBody>
      </CCard>
      <template #footer>
        <CButton color="secondary" @click="addTicketModal = false">Cancel</CButton>
        <CButton color="primary" :disabled="isSavingTicket" @click="saveTicket">
          <CSpinner v-if="isSavingTicket" size="sm" />
          <span v-else>Save Ticket</span>
        </CButton>
      </template>
    </CModal>

    <!-- Result Modal -->
    <CModal :show.sync="resultModal.show" :color="resultModal.color" :title="resultModal.title">
      {{ resultModal.message }}
      <template #footer>
        <CButton :color="resultModal.color" @click="resultModal.show = false">OK</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Tickets',
  data () {
    return {
      tickets: [],
      isLoading: false,
      errorMessage: '',
      currentPage: 1,
      totalPages: 1,
      pageInput: '1',
      ticketFields: ['id', 'ticket_type', 'customer_mood', 'problem', 'created_at'],
      addTicketModal: false,
      isSavingTicket: false,
      resultModal: { show: false, title: '', message: '', color: 'success' },
      newTicket: {
        product_used: '',
        crop: '',
        ticket_type: '',
        reason: '',
        customer_mood: '',
        is_fertilizer_user: false,
        description: ''
      },
      productOptions: ['THATCH', 'MIGRANIX', 'KNORHIN', 'STAM'],
      ticketTypeOptions: ['No Result', 'Result', 'Other Complaint'],
      reasonOptions: ['Improper usage', 'Course Not Completed', 'Continuing', 'Other'],
      moodOptions: ['Angry', 'Very Angry', 'Cool', 'Okay', 'Satisfied', 'Happy']
    }
  },
  methods: {
    token () { return localStorage.getItem('api_token') },
    fetchTickets () {
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/tickets?page=${this.currentPage}&token=${this.token()}`)
        .then(res => {
          const payload = res.data || {}
          this.tickets = Array.isArray(payload.data) ? payload.data : []
          this.currentPage = Number(payload.current_page || 1)
          this.totalPages = Number(payload.last_page || 1)
          this.pageInput = String(this.currentPage)
        })
        .catch(err => {
          console.log(err)
          this.errorMessage = 'Unable to load tickets.'
          this.tickets = []
        })
        .finally(() => { this.isLoading = false })
    },
    onPageChange (page) { this.currentPage = Number(page); this.pageInput = String(page); this.fetchTickets() },
    goToPage () {
      const p = Number(this.pageInput)
      if (!Number.isInteger(p) || p < 1) { this.pageInput = String(this.currentPage); return }
      const target = Math.min(p, this.totalPages)
      this.currentPage = target
      this.pageInput = String(target)
      this.fetchTickets()
    },
    openAddTicketModal () {
      this.addTicketModal = true
      this.newTicket = { product_used: '', crop: '', ticket_type: '', reason: '', customer_mood: '', is_fertilizer_user: false, description: '' }
    },
    saveTicket () {
      this.isSavingTicket = true
      const payload = {
        contact_id: null,
        product_used: this.newTicket.product_used,
        crop: this.newTicket.crop,
        ticket_type: this.newTicket.ticket_type,
        reason: this.newTicket.reason,
        customer_mood: this.newTicket.customer_mood,
        is_fertilizer_user: this.newTicket.is_fertilizer_user ? 1 : 0,
        description: this.newTicket.description
      }
      axios.post(`${this.$apiAdress}/api/tickets?token=${this.token()}`, payload)
        .then(res => {
          const data = res.data || {}
          this.resultModal = { show: true, title: data.title || 'Success', message: data.message || 'Ticket created', color: data.color || 'success' }
          this.addTicketModal = false
          this.fetchTickets()
        })
        .catch(err => {
          const data = err?.response?.data || {}
          const validation = data?.error && typeof data.error === 'object' ? Object.values(data.error).flat().join(', ') : ''
          this.resultModal = { show: true, title: data.title || 'Error', message: data.message || validation || 'Unable to create ticket.', color: data.color || 'warning' }
        })
        .finally(() => { this.isSavingTicket = false })
    }
  },
  mounted () {
    this.fetchTickets()
  }
}
</script>
